import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  supplierService, 
  Supplier, 
  SupplierFormData, 
  SupplierListParams 
} from '@/services/supplier/supplierService';

export interface SupplierState {
  suppliers: Supplier[];
  supplier: Supplier | null;
  suppliersLoading: boolean;
  isLoading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  } | null;
}

const initialState: SupplierState = {
  suppliers: [],
  supplier: null,
  suppliersLoading: false,
  isLoading: false,
  error: null,
  pagination: null,
};

export const fetchSuppliers = createAsyncThunk(
  'supplier/fetchSuppliers',
  async (params: SupplierListParams, { rejectWithValue }) => {
    try {
      const response = await supplierService.getSuppliers(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch suppliers');
    }
  }
);

export const createSupplier = createAsyncThunk(
  'supplier/createSupplier',
  async (data: SupplierFormData, { rejectWithValue }) => {
    try {
      const response = await supplierService.createSupplier(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create supplier');
    }
  }
);

export const updateSupplier = createAsyncThunk(
  'supplier/updateSupplier',
  async ({ id, data }: { id: string; data: SupplierFormData }, { rejectWithValue }) => {
    try {
      const response = await supplierService.updateSupplier(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update supplier');
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  'supplier/deleteSupplier',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await supplierService.deleteSupplier(id);
      return { ...response, id };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete supplier');
    }
  }
);

export const fetchSupplierById = createAsyncThunk(
  'supplier/fetchSupplierById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await supplierService.getSupplierById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch supplier');
    }
  }
);

export const supplierSlice = createSlice({
  name: 'supplier',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSupplier: (state) => {
      state.supplier = null;
    },
    setSuppliers: (state, action: PayloadAction<Supplier[]>) => {
      state.suppliers = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Suppliers
      .addCase(fetchSuppliers.pending, (state) => {
        state.suppliersLoading = true;
        state.error = null;
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        state.suppliersLoading = false;
        state.suppliers = action.payload.data.docs;
        state.pagination = {
          currentPage: action.payload.data.page,
          totalPages: action.payload.data.totalPages,
          totalItems: action.payload.data.totalDocs,
          itemsPerPage: action.payload.data.limit,
        };
        state.error = null;
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.suppliersLoading = false;
        state.error = action.payload as string;
      })
      
      // Create Supplier
      .addCase(createSupplier.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        state.isLoading = false;
        state.suppliers.unshift(action.payload.data);
        state.error = null;
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Update Supplier
      .addCase(updateSupplier.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedSupplier = action.payload.data;
        const index = state.suppliers.findIndex(supplier => supplier._id === updatedSupplier._id);
        if (index !== -1) {
          state.suppliers[index] = updatedSupplier;
        }
        state.error = null;
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Delete Supplier
      .addCase(deleteSupplier.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSupplier.fulfilled, (state, action) => {
        state.isLoading = false;
        state.suppliers = state.suppliers.filter(supplier => supplier._id !== action.payload.id);
        state.error = null;
      })
      .addCase(deleteSupplier.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Supplier By ID
      .addCase(fetchSupplierById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSupplierById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.supplier = action.payload.data;
        state.error = null;
      })
      .addCase(fetchSupplierById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSupplier, setSuppliers } = supplierSlice.actions;
export default supplierSlice.reducer;